import React, { useEffect, useState } from 'react'
import { FaEdit, FaTrash, FaTimes, FaInfoCircle } from 'react-icons/fa'
import { baseUrl } from '../../Environments/environment'
import { apiClient } from '../../api'
import { Notification, ConfirmationModal, useNotification, useConfirmation } from '../NotificationComponent/Notifications'

const TierType = ({ isOpen, onClose, tierData, action, onDataChange }) => {
  const [isCreateMode, setIsCreateMode] = useState(action === 'create')
  const [isUpdateMode, setIsUpdateMode] = useState(action === 'update')
  
  const [formData, setFormData] = useState({
    priority: tierData?.priority || '',
    tierType: tierData?.tierType || ''
  })

  const [isEditing, setIsEditing] = useState(action === 'create') // Start in edit mode for create, view mode for update
  const [isLoading, setIsLoading] = useState(false)
  const [showInfoSheet, setShowInfoSheet] = useState(false)
  
  // Custom hooks for notifications and confirmations
  const { showNotification, notificationMessage, notificationType, showNotificationMessage, hideNotification } = useNotification()
  const { showConfirm, confirmTitle, confirmMessage, isLoading: confirmLoading, showConfirmation, handleConfirm, handleCancel } = useConfirmation()

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleInfoClick = () => {
    setShowInfoSheet(true)
  }

  const handleInfoClose = () => {
    setShowInfoSheet(false)
  }

  const validateForm = () => {
    if (!formData.priority || !formData.tierType) {
      showNotificationMessage("Please fill in all required fields (Priority and Tier Type)", 'error');
      return false;
    }
    return true;
  }

  const handleUpdate = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      const propertyId = localStorage.getItem("selectedPropertyId");
      const token = localStorage.getItem("accessToken");
      
      if (isCreateMode) {
        // Create new tier type
        const response = await apiClient.post(
          `${baseUrl}/upgradeRoomWorkflow/tierData/${propertyId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
              if (response.status === 200 || response.status === 201) {
                console.log("Tier type created successfully:", response.data);
                showNotificationMessage("Tier type created successfully!", 'success');
                onDataChange && onDataChange(); // Notify parent to refresh data
                setTimeout(() => onClose(), 1500);
              }
      } else if (isUpdateMode) {
        // Update existing tier type
        formData.id = tierData.id;
        console.log("formData :", formData);
        console.log("tierData :", tierData);
        const response = await apiClient.put(
          `${baseUrl}/upgradeRoomWorkflow/tierData/${propertyId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
              if (response.status === 200) {
                console.log("Tier type updated successfully:", response.data);
                showNotificationMessage("Tier type updated successfully!", 'success');
                onDataChange && onDataChange(); // Notify parent to refresh data
                setTimeout(() => onClose(), 1500);
              }
      }
    } catch (error) {
      console.error("Error saving tier type:", error);
      showNotificationMessage("Error saving tier type. Please try again.", 'error');
    } finally {
      setIsLoading(false);
    }
  }

  const handleDeleteClick = () => {
    const deleteAction = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const data = {
          id: tierData.id,
          tierType: tierData.tierType
        }
        console.log("data :", data);
        const propertyId = localStorage.getItem("selectedPropertyId");
        const response = await apiClient.delete(
          `${baseUrl}/upgradeRoomWorkflow/tierData/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            data,
          }
        );
        
        if (response.status === 200) {
          console.log("Tier type deleted successfully");
          showNotificationMessage("Tier type deleted successfully!", 'success');
          onDataChange && onDataChange(); // Notify parent to refresh data
          setTimeout(() => onClose(), 1500);
        }
      } catch (error) {
        console.error("Error deleting tier type:", error);
        showNotificationMessage("Error deleting tier type. Please try again.", 'error');
      }
    }

    showConfirmation(
      "Confirm Deletion",
      `Are you sure you want to delete <span class="font-semibold text-gray-900">"${formData.tierType}"</span>? This action cannot be undone.`,
      deleteAction
    );
  }

  // Update states when action changes
  useEffect(() => {
    setIsCreateMode(action === 'create')
    setIsUpdateMode(action === 'update')
    setIsEditing(action === 'create') // Start in edit mode for create, view mode for update
  }, [action])

  if (!isOpen) return null

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">{isCreateMode ? 'Add New Tier Type' : 'Edit Tier Type'}</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Priority */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 flex items-center">
                Priority
                <button
                  onClick={handleInfoClick}
                  className="ml-2 text-blue-500 hover:text-blue-700 transition-colors"
                  type="button"
                >
                  <FaInfoCircle className="w-4 h-4" />
                </button>
              </label>
              {isEditing ? (
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter priority"
                  min="1"
                />
              ) : (
                <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                  {formData.priority || 'Not specified'}
                </div>
              )}
            </div>

            {/* Tier Type */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Tier Type
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.tierType}
                  onChange={(e) => handleInputChange('tierType', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter tier type"
                />
              ) : (
                <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                  {formData.tierType || 'Not specified'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
          <div className="flex space-x-3">
            {!isEditing && isUpdateMode ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                <FaEdit className="w-4 h-4 mr-2" />
                Edit
              </button>
            ) : isEditing ? (
              <button
                onClick={() => {
                    if(isCreateMode){
                        onClose()
                    }else{
                        setIsEditing(false)
                    }
                }}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                <FaTimes className="w-4 h-4 mr-2" />
                Cancel
              </button>
            ) : null}
          </div>
          
          <div className="flex space-x-3">
            {isEditing ? (
              <button
                onClick={handleUpdate}
                className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <FaEdit className="w-4 h-4 mr-2" />
                )}
                {isCreateMode ? 'Create' : 'Update'}
              </button>
            ) : isUpdateMode ? (
              <button
                onClick={handleDeleteClick}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <FaTrash className="w-4 h-4 mr-2" />
                )}
                Delete
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Notification */}
      <Notification
        show={showNotification}
        message={notificationMessage}
        type={notificationType}
        onClose={hideNotification}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showConfirm}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isLoading={confirmLoading}
      />

      {/* Info Sheet */}
      {showInfoSheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <FaInfoCircle className="w-5 h-5 text-blue-500 mr-2" />
                  Priority Information
                </h3>
                <button
                  onClick={handleInfoClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  The <strong>Priority</strong> field determines the order in which tier types are displayed and processed.
                </p>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">Priority Rules:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Lower numbers = Higher priority (1 is highest)</li>
                    <li>• Must be a positive integer</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">Example:</h4>
                  <p className="text-sm text-gray-700">
                    Priority 1 = "Premium Tier" (shown first)<br/>
                    Priority 2 = "Standard Tier" (shown second)<br/>
                    Priority 3 = "Basic Tier" (shown last)
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleInfoClose}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  )
}

export default TierType
